<template>
  <span class="sello" :class="'sello--' + estadoReal" :title="texto">
    <span class="sello__punto"></span>
    <span class="sello__texto">{{ texto }}<template v-if="nota"> &middot; {{ nota }}</template></span>
  </span>
</template>

<script>
/**
 * Sello "de cuándo son los datos que enseña esta pantalla" (#16). Ninguna pantalla se queda
 * callada: son TRES estados y los tres se ven, porque sin el de "en vivo" una pantalla que lee
 * en directo y una cuya caché no guarda fecha se verían igual.
 *
 *   fecha       → se sirve de una caché y esa caché sabe cuándo se publicó.
 *   vivo        → se calcula en la propia petición, sin pasar por caché.
 *   desconocido → se sirve de caché, pero esa caché no guarda marca de recálculo.
 *
 * Nunca se inventa una fecha: con estado "fecha" y `fecha` vacía se degrada a "desconocido" en
 * vez de pintar la hora de la consulta, que no dice nada de la frescura del dato.
 *
 * Es el gemelo en Vue 2 de crm-new/src/components/SelloDatos.vue. El código no se comparte
 * (son dos proyectos con build propio) pero el texto y los tres estados sí: quien mira las dos
 * herramientas tiene que leer lo mismo.
 */
export default {
  name: 'SelloDatos',
  props: {
    estado: {
      type: String,
      required: true,
      validator: function(v) { return ['fecha', 'vivo', 'desconocido'].indexOf(v) !== -1; }
    },
    // Date, String ISO o el número de milisegundos que devuelva la API; null si no se sabe.
    fecha: { type: [String, Number, Date], default: null },
    /** Coletilla para las pantallas mixtas: "Datos en vivo · <nota>". */
    nota: { type: String, default: null }
  },
  computed: {
    estadoReal() {
      return this.estado === 'fecha' && !this.fecha ? 'desconocido' : this.estado;
    },
    texto() {
      if (this.estadoReal === 'vivo') return 'Datos en vivo';
      if (this.estadoReal === 'desconocido') return 'No se sabe de cuándo son estos datos';
      return 'Último recálculo: ' + this.fechaHora;
    },
    fechaHora() {
      var d = this.fecha instanceof Date ? this.fecha : new Date(this.fecha);
      // El análisis de diferencias publica su marca como "yyyy-MM-dd HH:mm", que Safari no
      // parsea. Se reintenta en ISO antes de darla por perdida.
      if (isNaN(d.getTime()) && typeof this.fecha === 'string') {
        d = new Date(this.fecha.replace(' ', 'T'));
      }
      // Si aun así no se puede leer, se enseña el original tal cual: es una marca real aunque
      // no sepamos darle formato, y pintar "Invalid Date" sería peor.
      if (isNaN(d.getTime())) return String(this.fecha);
      return d.toLocaleString('es-ES', {
        day: '2-digit', month: '2-digit', year: 'numeric',
        hour: '2-digit', minute: '2-digit'
      });
    }
  }
};
</script>

<style scoped>
/* Sin nowrap y con tope de ancho: las pastillas con coletilla larga no cabían en el viewport de
   un móvil y desbordaban la pantalla a lo ancho. Se parten en varias líneas en vez de recortarse:
   el sello dice de cuándo son los datos, así que tiene que poder leerse entero. Mismo criterio
   que en el gemelo de crm-new. */
.sello {
  display: inline-flex;
  align-items: flex-start;
  gap: 6px;
  padding: 2px 10px;
  border-radius: 12px;
  font-size: 11px;
  line-height: 1.6;
  max-width: 100%;
  border: 1px solid transparent;
  vertical-align: middle;
}
.sello__punto {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: currentColor;
  flex-shrink: 0;
  /* Alineado con la PRIMERA línea del texto, no con el bloque, cuando el sello se parte. */
  margin-top: calc((1.6em - 6px) / 2);
}
.sello__texto {
  min-width: 0;
  overflow-wrap: anywhere;
}
.sello--fecha {
  background: #eef2f9;
  border-color: #d5deec;
  color: #40506e;
}
.sello--vivo {
  background: #e7f6ef;
  border-color: #c6e8d8;
  color: #0f6e56;
}
/* "No se sabe" no es un error, pero tampoco puede pasar desapercibido: ámbar, no rojo. */
.sello--desconocido {
  background: #fff6e2;
  border-color: #f2ddb0;
  color: #8a5a00;
}
</style>
